import {
  NoiseCancellationSettingsRequestDto,
  NoiseCancellationSettingsResponseDto,
} from '@shared/services/apiService/apiService';

export type NoiseCancellationDraft = {
  configured: boolean;
  provider: string;
  strength: string;
  recordAudio: boolean;
  noiseDetection: string;
};

export type NoiseCancellationSettingsDraftRequest = Omit<
  NoiseCancellationSettingsRequestDto,
  'enabled'
>;

export type NoiseCancellationValidation = {
  enabled?: string;
  provider?: string;
  strength?: string;
  triggers?: string;
  noiseDetection?: string;
};

const stringifyJson = (value?: object | null) =>
  value == null ? '' : JSON.stringify(value, null, 2);

const mapCancellationToDraft = (
  value?: NoiseCancellationSettingsResponseDto | null,
): NoiseCancellationDraft => ({
  configured: Boolean(value),
  provider: value?.provider ?? '',
  strength: value?.strength === undefined ? '' : String(value.strength),
  recordAudio: Boolean(value?.recordAudio),
  noiseDetection: stringifyJson(value?.noiseDetection),
});

export const mapNoiseCancellationToDraft = (value?: NoiseCancellationSettingsResponseDto | null) =>
  mapCancellationToDraft(value);

const parseJsonObject = (value: string) => {
  if (!value.trim()) return { value: undefined, error: undefined };

  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { value: undefined, error: 'noiseProcessing.validation.jsonObject' };
    }
    return { value: parsed as Record<string, unknown>, error: undefined };
  } catch {
    return { value: undefined, error: 'noiseProcessing.validation.invalidJson' };
  }
};

export const validateNoiseCancellationDraft = (
  draft: NoiseCancellationDraft,
): NoiseCancellationValidation => {
  if (!draft.configured) return {};

  const errors: NoiseCancellationValidation = {};
  if (!draft.provider.trim()) errors.provider = 'noiseProcessing.validation.providerRequired';

  if (draft.strength.trim()) {
    const strength = Number(draft.strength);
    if (!Number.isFinite(strength) || strength < 0 || strength > 1) {
      errors.strength = 'noiseProcessing.validation.strengthRange';
    }
  }

  const noiseDetection = parseJsonObject(draft.noiseDetection);
  if (noiseDetection.error) errors.noiseDetection = noiseDetection.error;

  return errors;
};

const mapCancellationToRequest = (
  draft: NoiseCancellationDraft,
): NoiseCancellationSettingsDraftRequest | null => {
  if (!draft.configured) return null;

  const request: NoiseCancellationSettingsDraftRequest = {
    provider: draft.provider.trim(),
    recordAudio: draft.recordAudio,
  };
  if (draft.strength.trim()) request.strength = Number(draft.strength);

  const noiseDetection = parseJsonObject(draft.noiseDetection);
  if (noiseDetection.value) request.noiseDetection = noiseDetection.value;

  return request;
};

export const mapNoiseCancellationDraftToRequest = (
  draft: NoiseCancellationDraft,
): NoiseCancellationSettingsDraftRequest | null => mapCancellationToRequest(draft);

export const cloneNoiseCancellationDraft = (
  draft: NoiseCancellationDraft,
): NoiseCancellationDraft => ({ ...draft });
